import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {Box, Select, MenuItem} from '@mui/material';
import {snakeCaseToCapitalizedCase} from '../../utils/miscellaneous';
import {useTheme} from '@mui/material/styles';
import {useState, useMemo} from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as React from 'react';
import {StaticDateRangePicker} from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import {pickersLayoutClasses} from '@mui/x-date-pickers/PickersLayout';

export const temporaryMetricTitleFormatWrapper = metricKey => {
  const cases = {
    stress_index: 'Mental Stress Index',
    respiration: 'Breathing Rate',
    heart_disease_risk: 'Heart Attack Risk',
    cardiovascular_risk: 'Cardiovascular Disease Risk',
    hypercholesteremia_risk: 'Hypercholesterolemia Risk',
    hba1c: 'Risk of BhA1C > 5.7%',
    blood_glucose: 'Risk of Fasting Blood Glucose > 5.5 mmol/L',
    diabetes_risk: 'Type II Diabetes Risk',
    overall_wellness: 'Overall Health Score',
  };

  const output = cases[metricKey] || snakeCaseToCapitalizedCase(metricKey);
  return output;
};

/**
 * Custom Select Dropdown Component
 * Select component that will have a dropdown with options in which you can only choose one, that chosen value is saved on a variable and then managed by an onChanged event listener
 * 👁️ this element won't handle its own width but take the width of its parent component
 *
 * @param {Object} props - Component props
 * @param {string} props.value - useState getter that will save the value of this component
 * @param {string} props.label - An initial piece of text that will be shown in the dropdown when there's no data, in order for it to work the initial value of props.value should be ""
 * @param {function} props.onChange - Callback that happens when the value of the dropdown changes
 * @param {boolean} props.disabled - Boolean to determine if the element is disabled or not
 * @param {string[]} props.options - Array of string values that this component will require, right now those will be used as the values for each select item and are going to be formatted in a special way to show as the label
 *
 * @returns {React.Component} Rendered Component
 */
export const CustomSelect = ({options, label, value, onChange, disabled}) => {
  const theme = useTheme();
  const mainColor = '#000000';
  const customSelectStyles = useMemo(() => {
    return {
      backgroundColor: 'transparent',
      color: mainColor,
      boxSizing: 'border-box',
      height: 'fit-content',
      '& .MuiSelect-select': {
        px: 2,
      },
      '&:focus': {
        backgroundColor: 'transparent !important',
        '&::after': {
          borderBottom: `2px solid ${mainColor} !important`,
        },
      },
      '&::after': {
        borderBottom: `2px solid ${mainColor} !important`,
      },
      '&::before': {
        borderBottom: `2px solid ${mainColor}`,
      },
      '&:hover': {
        backgroundColor: 'transparent',
        '&::before': {
          borderBottom: `3px solid ${mainColor} !important`,
        },
      },
      '& .MuiSvgIcon-root': {
        color: mainColor,
      },
    };
  }, [theme]);

  return (
    <Select
      sx={customSelectStyles}
      value={value}
      fullWidth
      onChange={onChange}
      disabled={disabled}
      variant="standard"
      displayEmpty
      IconComponent={ExpandMoreIcon}
    >
      <MenuItem value="" sx={{display: 'none', color: mainColor}}>
        {label}
      </MenuItem>
      {options.map((opt, idx) => (
        <MenuItem key={idx} value={opt} sx={{color: mainColor}}>
          {temporaryMetricTitleFormatWrapper(opt)}
        </MenuItem>
      ))}
    </Select>
  );
};

export const CustomDateRangePicker = ({onDateRangeChange, defaultDate, isLoading}) => {
  const theme = useTheme();
  const [dateRange, setDateRange] = useState([defaultDate.startDate, defaultDate.endDate]);
  const [dateRangeSelected, setDateRangeSelected] = useState(false);
  const mainColor = '#000000';

  const customSelectStyles = useMemo(() => {
    return {
      backgroundColor: 'transparent',
      color: mainColor,
      boxSizing: 'border-box',
      height: 'fit-content',
      '& .MuiSelect-select': {
        px: 2,
      },
      '&:focus': {
        backgroundColor: 'transparent !important',
        '&::after': {
          borderBottom: `2px solid ${mainColor} !important`,
        },
      },
      '&::after': {
        borderBottom: `2px solid ${mainColor} !important`,
      },
      '&::before': {
        borderBottom: `2px solid ${mainColor} !important`,
      },
      '&:hover': {
        backgroundColor: 'transparent',
        '&::before': {
          borderBottom: `2px solid ${mainColor} !important`,
        },
      },
      '& .MuiSvgIcon-root': {
        color: mainColor,
      },
    };
  }, [theme]);

  const handleDateRangeChange = newDateRange => {
    setDateRange([newDateRange[0], newDateRange[1]]);
    setDateRangeSelected(true);

    if (onDateRangeChange) {
      onDateRangeChange(newDateRange);
    }
  };

  /* format of the string shown in the first element of the select box
     depending on whether tha page is loading and the date range selected
  */
  const firstMenuItem = useMemo(() => {
    const selectedStartDate = dateRange[0];
    const selectedEndDate = dateRange[1];
    if (isLoading) return 'Select timeframe';

    if (!dateRangeSelected) return dayjs(defaultDate.startDate, 'MMM DD, yyyy').add(1, 'day').format('LL');

    if (!selectedStartDate || !selectedEndDate) return 'Select timeframe';

    if (selectedStartDate.isSame(selectedEndDate, 'day')) return selectedStartDate.format('LL');

    return `${selectedStartDate.format('LL')} - ${selectedEndDate.format('LL')}`;
  }, [isLoading, dateRange, dateRangeSelected]);

  return (
    <Box>
      <Select
        sx={customSelectStyles}
        fullWidth
        variant="standard"
        displayEmpty
        IconComponent={ExpandMoreIcon}
        value={null}
      >
        <MenuItem value={null} sx={{color: mainColor}}>
          {firstMenuItem}
        </MenuItem>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StaticDateRangePicker
            disableFuture
            value={dateRange}
            onChange={handleDateRangeChange}
            onAccept={() => {
              setDateRangeSelected(true);
            }}
            onClose={() => {
              setDateRangeSelected(false);
            }}
            sx={{
              [`.${pickersLayoutClasses.toolbar}`]: {
                color: `${mainColor} !important`,
              },
              [`.${pickersLayoutClasses.toolbar} .css-1r7itxx-MuiTypography-root-MuiPickersToolbarText-root.Mui-selected`]:
                {
                  color: `${mainColor} !important`,
                },
              [`.${pickersLayoutClasses.toolbar} .css-1r7itxx-MuiTypography-root-MuiPickersToolbarText-root, .${pickersLayoutClasses.toolbar} .MuiTypography-root`]:
                {
                  color: `${mainColor} !important`,
                },
              [`.${pickersLayoutClasses.contentWrapper}`]: {
                alignItems: 'center',
                color: mainColor,
              },
            }}
            slotProps={{actionBar: {actions: []}}}
          />
        </LocalizationProvider>
      </Select>
    </Box>
  );
};
